import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Clock, Heart, Send, Filter, ChevronDown, ArrowUpDown } from 'lucide-react';
import LastSyncIndicator from '../../components/good-things/LastSyncIndicator';
import { refreshManager } from '../../services/refreshManager';
import '../../styles/select-hover.css';
import { GoodThingCard } from '../../components/good-things/GoodThingCard';
import { goodThingsService } from '../../services/goodThings';
import { metricsCache } from '../../services/metricsCache';
import { GoodThing } from '../../types/good-things';
import { FilterState, DATE_PRESETS } from '../../types/filters';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/ui/use-toast';
import { GoodThingDialog } from '../../components/good-things/GoodThingDialog';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { DatePresetSelector } from '../../components/good-things/DatePresetSelector';
import { GoodThingsGrid } from '../../components/good-things/GoodThingsGrid';
import { GoodThingsLine } from '../../components/good-things/GoodThingsLine';
import { LayoutToggle, LayoutType } from '../../components/good-things/LayoutToggle';
import { employeeNamesService } from '../../services/employeeNames';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

// Constants
const ALL_CATEGORIES = 'all';
const ALL_PEOPLE = 'all';
const SORT_NEWEST = 'newest';
const SORT_OLDEST = 'oldest';

const RecentUserGoodThings: React.FC<{ 
  goodThings: GoodThing[],
  isLoading: boolean,
  isLoadingMore: boolean,
  hasMore: boolean,
  onLoadMore: () => void,
  layoutType: LayoutType,
  onEdit?: () => void,
  onDelete?: () => void
}> = ({ goodThings, isLoading, isLoadingMore, hasMore, onLoadMore, layoutType, onEdit, onDelete }) => {
  const loadingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadingRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoadingMore, onLoadMore]);

  if (goodThings.length === 0 && !isLoading) {
    return (
      <div className="text-gray-500 dark:text-gray-400 text-center py-4 lg:col-span-2">
        No recent Good Things found. Start adding some!
      </div>
    );
  }

  return layoutType === 'grid' ? (
    <GoodThingsGrid>
      {goodThings.map((goodThing) => (
        <GoodThingCard 
          key={goodThing.id} 
          goodThing={goodThing} 
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
      {isLoadingMore && (
        <Card className="lg:col-span-2">
          <CardContent className="p-3 md:p-6">
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      )}
      <div ref={loadingRef} className="h-8 lg:col-span-2" />
      {!hasMore && goodThings.length > 0 && (
        <div className="text-center text-gray-500 dark:text-gray-400 text-xs md:text-sm italic mt-2 lg:col-span-2">
          🎉 That's all folks! You've reached the end of your Good Things journey... for now! 🌟
        </div>
      )}
    </GoodThingsGrid>
  ) : (
    <GoodThingsLine>
      {goodThings.map((goodThing) => (
        <GoodThingCard 
          key={goodThing.id} 
          goodThing={goodThing} 
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
      {isLoadingMore && (
        <Card>
          <CardContent className="p-3 md:p-6">
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      )}
      <div ref={loadingRef} className="h-8" />
      {!hasMore && goodThings.length > 0 && (
        <div className="text-center text-gray-500 dark:text-gray-400 text-xs md:text-sm italic mt-2">
          🎉 That's all folks! You've reached the end of your Good Things journey... for now! 🌟
        </div>
      )}
    </GoodThingsLine>
  );
};

// MetricCard component for displaying metrics
const MetricCard = ({ icon: Icon, label, value, color }: { icon: any, label: string, value: React.ReactNode, color: string }) => {
  const iconColor = color.replace('bg-', 'text-').replace('-100', '-500');
  const darkIconColor = color.replace('bg-', 'text-').replace('-100', '-300');
  
  return (
    <Card className="relative overflow-hidden">
      <div className={`absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6`}>
        <div className={`absolute inset-0 ${color} dark:${color.replace('-100', '-900')} rounded-full opacity-20 dark:opacity-30`}></div>
      </div>
      <CardContent className="p-4 flex flex-col items-center justify-center text-center">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`h-4 w-4 ${iconColor} dark:${darkIconColor}`} />
          <h3 className="font-medium text-gray-600 dark:text-gray-400 text-xs">{label}</h3>
        </div>
        <div className="text-xl font-bold text-gray-900 dark:text-white">{value}</div>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  usePageTitle('Good Thing Tracker');
  const { isAuthenticated, isLoading: authLoading, userEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userGoodThings, setUserGoodThings] = useState<GoodThing[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [metrics, setMetrics] = useState({
    receivedCount: 0,
    enteredCount: 0,
    daysSinceLastEntry: 'Never'
  });
  const [activeView, setActiveView] = useState<'received' | 'entered'>('received');
  const [layoutType, setLayoutType] = useState<LayoutType>('grid');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>(SORT_NEWEST);
  const [filters, setFilters] = useState<FilterState>({
    dateRange: {
      from: '',
      to: ''
    },
    datePreset: DATE_PRESETS.ALL_DATES,
    category: ALL_CATEGORIES,
    enterer: ALL_PEOPLE,
    recipient: ALL_PEOPLE
  });
  const [categories, setCategories] = useState<string[]>([]);
  const [enterers, setEnterers] = useState<string[]>([]);
  const currentPage = useRef(1);
  const ITEMS_PER_PAGE = 6; // Increased to 6 to better fill the grid
  
  // Track if this is the first load
  const isFirstLoad = useRef(true);
  
  // Track if metrics are being loaded
  const isLoadingMetrics = useRef(false);

  // Format name from email
  const formatName = (email: string): string => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  // Function to fetch Good Things entered by the user
  const fetchUserEnteredGoodThings = useCallback(async (
    userName: string,
    limit: number = ITEMS_PER_PAGE,
    offset: number = 0
  ): Promise<GoodThing[]> => {
    try {
      // Get all entries
      const allEntries = await goodThingsService.getAllEntries();
      
      // Create name variations to handle different formats
      const nameVariations = [userName];
      if (userName.endsWith('.')) {
        nameVariations.push(userName.slice(0, -1));
      } else {
        nameVariations.push(`${userName}.`);
      }
      
      // Filter entries submitted by the user
      let userEntries = allEntries.filter(entry => 
        nameVariations.some(name => entry.submitter === name)
      );
      
      // Apply category filter if needed
      if (filters.category && filters.category !== ALL_CATEGORIES) {
        userEntries = userEntries.filter(entry => entry.category === filters.category);
      }

      // Apply date range filter if needed
      if (filters.dateRange.from && filters.dateRange.to) {
        const fromDate = new Date(filters.dateRange.from);
        const toDate = new Date(filters.dateRange.to);
        
        // Skip date filtering if ALL_DATES is selected
        if (!(fromDate.getFullYear() === 1970 && toDate.getFullYear() === new Date().getFullYear())) {
          userEntries = userEntries.filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate >= fromDate && entryDate <= toDate;
          });
        }
      }

      // Sort by date
      let sortedEntries = [...userEntries];
      if (sortOrder === SORT_NEWEST) {
        sortedEntries.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
      } else {
        sortedEntries.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        });
      }
      
      // Apply pagination
      return sortedEntries.slice(offset, offset + limit);
    } catch (error) {
      return [];
    }
  }, [filters, sortOrder]);

  // Function to fetch Good Things received by the user
  const fetchUserReceivedGoodThings = useCallback(async (
    userEmail: string,
    limit: number = ITEMS_PER_PAGE,
    offset: number = 0
  ): Promise<GoodThing[]> => {
    try {
      // Get all entries
      const allEntries = await goodThingsService.getAllEntries();
      
      // Get user name from email
      const userName = await employeeNamesService.getNameFromEmail(userEmail);
      if (!userName) {
        return [];
      }
      
      // Create name variations to handle different formats
      const nameVariations = [userName];
      if (userName.endsWith('.')) {
        nameVariations.push(userName.slice(0, -1));
      } else {
        nameVariations.push(`${userName}.`);
      }
      
      // Filter entries where the user is a recipient
      let userReceivedEntries = allEntries.filter(entry => 
        entry.recipients && entry.recipients.some(recipient => 
          nameVariations.some(name => recipient === name)
        )
      );

      // Extract unique enterers for the filter dropdown
      const uniqueEnterers = [...new Set(userReceivedEntries.map(entry => entry.submitter))];
      setEnterers(uniqueEnterers);

      // Extract unique categories for the filter dropdown
      const uniqueCategories = [...new Set(userReceivedEntries.map(entry => entry.category))];
      setCategories(uniqueCategories);

      // Apply enterer filter if needed
      if (filters.enterer && filters.enterer !== ALL_PEOPLE) {
        userReceivedEntries = userReceivedEntries.filter(entry => entry.submitter === filters.enterer);
      }

      // Apply category filter if needed
      if (filters.category && filters.category !== ALL_CATEGORIES) {
        userReceivedEntries = userReceivedEntries.filter(entry => entry.category === filters.category);
      }

      // Apply date range filter if needed
      if (filters.dateRange.from && filters.dateRange.to) {
        const fromDate = new Date(filters.dateRange.from);
        const toDate = new Date(filters.dateRange.to);
        
        // Skip date filtering if ALL_DATES is selected
        if (!(fromDate.getFullYear() === 1970 && toDate.getFullYear() === new Date().getFullYear())) {
          userReceivedEntries = userReceivedEntries.filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate >= fromDate && entryDate <= toDate;
          });
        }
      }
      
      // Sort by date
      let sortedEntries = [...userReceivedEntries];
      if (sortOrder === SORT_NEWEST) {
        sortedEntries.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
      } else {
        sortedEntries.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        });
      }
      
      // Apply pagination
      return sortedEntries.slice(offset, offset + limit);
    } catch (error) {
      return [];
    }
  }, [filters, sortOrder]);

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      dateRange: {
        from: '',
        to: ''
      },
      datePreset: DATE_PRESETS.ALL_DATES,
      category: ALL_CATEGORIES,
      enterer: ALL_PEOPLE,
      recipient: ALL_PEOPLE
    });
    setSortOrder(SORT_NEWEST);
    toast({
      title: 'Filters cleared',
      description: 'All filters have been reset to default values.',
      variant: 'default'
    });
  };

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const nextPage = currentPage.current + 1;
      const offset = (nextPage - 1) * ITEMS_PER_PAGE;
      
      let moreGoodThings: GoodThing[] = [];
      
      if (activeView === 'received') {
        moreGoodThings = await fetchUserReceivedGoodThings(
          userEmail!,
          ITEMS_PER_PAGE,
          offset
        );
      } else {
        const userName = await employeeNamesService.getNameFromEmail(userEmail!);
        if (userName) {
          moreGoodThings = await fetchUserEnteredGoodThings(
            userName,
            ITEMS_PER_PAGE,
            offset
          );
        }
      }

      setUserGoodThings(prev => [...prev, ...moreGoodThings]);
      setHasMore(moreGoodThings.length > 0);
      currentPage.current = nextPage;
    } catch (err) {
      setError('Failed to load more Good Things. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [userEmail, isLoadingMore, activeView, fetchUserEnteredGoodThings, fetchUserReceivedGoodThings, filters, sortOrder]);

  // Calculate metrics
  const calculateEnteredCount = (allEntries: GoodThing[], userName: string) => {
    // Create both versions of the name
    const nameVariations = [userName];
    if (userName.endsWith('.')) {
      // If name ends with period, also check without period
      nameVariations.push(userName.slice(0, -1));
    } else {
      // If name doesn't end with period, also check with period
      nameVariations.push(`${userName}.`);
    }
    
    // Check if any variation matches the submitter
    return allEntries.filter(entry => 
      nameVariations.some(name => entry.submitter === name)
    ).length;
  };

  const calculateDaysSinceLastEntry = (allEntries: GoodThing[], userName: string) => {
    // Create both versions of the name
    const nameVariations = [userName];
    if (userName.endsWith('.')) {
      nameVariations.push(userName.slice(0, -1));
    } else {
      nameVariations.push(`${userName}.`);
    }
    
    // Find entries submitted by any variation of the user's name
    const userEntries = allEntries.filter(entry => 
      nameVariations.some(name => entry.submitter === name)
    );
    
    if (userEntries.length === 0) return "Never";
    
    // Sort by date descending
    userEntries.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
    
    const mostRecentEntry = userEntries[0];
    const mostRecentDate = new Date(mostRecentEntry.date);
    const today = new Date();
    
    // Calculate difference in days
    const diffTime = today.getTime() - mostRecentDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays === 0 ? "Today" : diffDays === 1 ? "1 day" : `${diffDays} days`;
  };

  // Debounced refresh function to prevent multiple refreshes
  const debouncedRefresh = useCallback((fn: () => void) => {
    // If we already have a refresh queued, don't queue another one
    if (refreshManager.getStatus().pendingSyncs > 0) {
      return;
    }
    
    refreshManager.queueRefresh(fn);
  }, []);

  const fetchMetrics = useCallback(async () => {
    if (!isAuthenticated || !userEmail) return;
    
    try {
      setIsLoading(true);
      setError(null);

      // Get user display name
      const userName = await employeeNamesService.getNameFromEmail(userEmail);
      setDisplayName(userName || '');
      
      if (!userName) {
        setError('Failed to load user information. Please try again.');
        return;
      }

      // First, fetch the appropriate Good Things based on active view
      // This is the most important data to show quickly
      if (activeView === 'received') {
        const initialGoodThings = await fetchUserReceivedGoodThings(userEmail, ITEMS_PER_PAGE, 0);
        
        // Update the cache with the new data
        saveDashboardCache('received', initialGoodThings, filters, sortOrder);
        
        setUserGoodThings(initialGoodThings);
        setHasMore(initialGoodThings.length >= ITEMS_PER_PAGE);
      } else {
        const initialGoodThings = await fetchUserEnteredGoodThings(userName, ITEMS_PER_PAGE, 0);
        
        // Update the cache with the new data
        saveDashboardCache('entered', initialGoodThings, filters, sortOrder);
        
        setUserGoodThings(initialGoodThings);
        setHasMore(initialGoodThings.length >= ITEMS_PER_PAGE);
      }
      
      currentPage.current = 1;
      
      // Now that we've loaded the entries, we can set loading to false
      // so the user can see the entries while we calculate metrics
      setIsLoading(false);
      
      // Only calculate metrics if they're not already being calculated
      if (!isLoadingMetrics.current) {
        isLoadingMetrics.current = true;
        
        // Calculate metrics in the background
        setTimeout(async () => {
          try {
            // Get all entries for metrics
            const allEntries = await goodThingsService.getAllEntries();
            
            // Get total count of received Good Things
            const totalReceivedCount = await goodThingsService.getUserTotalGoodThingsCount(userEmail);
            
            // Calculate metrics
            const enteredCount = calculateEnteredCount(allEntries, userName);
            const daysSinceLastEntry = calculateDaysSinceLastEntry(allEntries, userName);
            
            // Update state with metrics
            setMetrics({
              receivedCount: totalReceivedCount,
              enteredCount,
              daysSinceLastEntry
            });
          } catch (err) {
            // Silently handle metrics calculation errors
          } finally {
            isLoadingMetrics.current = false;
          }
        }, 100); // Small delay to prioritize rendering the entries first
      }
    } catch (err) {
      setError('Failed to load your Good Things. Please try again.');
      setIsLoading(false);
    }
  }, [isAuthenticated, userEmail, activeView, fetchUserEnteredGoodThings, fetchUserReceivedGoodThings, filters, sortOrder]);

  // Cache TTL in milliseconds (5 minutes)
  const CACHE_TTL = 5 * 60 * 1000;
  const DASHBOARD_CACHE_KEY_RECEIVED = 'dashboardReceivedCache';
  const DASHBOARD_CACHE_KEY_ENTERED = 'dashboardEnteredCache';

  // Load cache from localStorage
  const loadDashboardCache = (view: 'received' | 'entered') => {
    try {
      const cacheKey = view === 'received' ? DASHBOARD_CACHE_KEY_RECEIVED : DASHBOARD_CACHE_KEY_ENTERED;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      
    }
    return null;
  };

  // Save cache to localStorage with size management
  const saveDashboardCache = (view: 'received' | 'entered', data: GoodThing[], filters: FilterState, sortOrder: string) => {
    try {
      const cacheKey = view === 'received' ? DASHBOARD_CACHE_KEY_RECEIVED : DASHBOARD_CACHE_KEY_ENTERED;
      
      // Limit cache to most recent 50 entries for dashboard (smaller than feed since it's user-specific)
      const limitedData = data.slice(0, 50);
      
      const cacheData = {
        data: limitedData,
        timestamp: Date.now(),
        filters,
        sortOrder,
        totalCount: data.length // Track original count
      };
      
      const serialized = JSON.stringify(cacheData);
      
      // Check if the serialized data is too large (>1MB as safety margin for dashboard)
      if (serialized.length > 1024 * 1024) {
        
        const reducedData = data.slice(0, 25);
        const reducedCacheData = {
          data: reducedData,
          timestamp: Date.now(),
          filters,
          sortOrder,
          totalCount: data.length
        };
        localStorage.setItem(cacheKey, JSON.stringify(reducedCacheData));
      } else {
        localStorage.setItem(cacheKey, serialized);
      }
      
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        
        // Clear other caches to make space
        localStorage.removeItem('feedEntriesCache');
        const otherCacheKey = view === 'received' ? DASHBOARD_CACHE_KEY_ENTERED : DASHBOARD_CACHE_KEY_RECEIVED;
        localStorage.removeItem(otherCacheKey);
        
        try {
          // Try with just the first 10 entries
          const minimalData = data.slice(0, 10);
          const minimalCacheData = {
            data: minimalData,
            timestamp: Date.now(),
            filters,
            sortOrder,
            totalCount: data.length
          };
          const currentCacheKey = view === 'received' ? DASHBOARD_CACHE_KEY_RECEIVED : DASHBOARD_CACHE_KEY_ENTERED;
          localStorage.setItem(currentCacheKey, JSON.stringify(minimalCacheData));
          
        } catch (retryError) {
          
          // Disable caching for this view
          const finalCacheKey = view === 'received' ? DASHBOARD_CACHE_KEY_RECEIVED : DASHBOARD_CACHE_KEY_ENTERED;
          localStorage.removeItem(finalCacheKey);
        }
      } else {
        
      }
    }
  };

  // Helper function to check if filters are effectively the same (ignoring default values)
  const areFiltersEffectivelyEqual = (cachedFilters: FilterState, currentFilters: FilterState) => {
    // For category, treat 'all' as equivalent to ALL_CATEGORIES
    const cachedCategory = cachedFilters.category === 'all' ? ALL_CATEGORIES : cachedFilters.category;
    const currentCategory = currentFilters.category === 'all' ? ALL_CATEGORIES : currentFilters.category;
    
    // For enterer, treat 'all' as equivalent to ALL_PEOPLE
    const cachedEnterer = cachedFilters.enterer === 'all' ? ALL_PEOPLE : cachedFilters.enterer;
    const currentEnterer = currentFilters.enterer === 'all' ? ALL_PEOPLE : currentFilters.enterer;
    
    // For date ranges, treat empty strings as equivalent
    const cachedDateFrom = cachedFilters.dateRange.from || '';
    const currentDateFrom = currentFilters.dateRange.from || '';
    const cachedDateTo = cachedFilters.dateRange.to || '';
    const currentDateTo = currentFilters.dateRange.to || '';
    
    return (
      cachedCategory === currentCategory &&
      cachedEnterer === currentEnterer &&
      cachedDateFrom === currentDateFrom &&
      cachedDateTo === currentDateTo
    );
  };

  // Check if cache is valid
  const isCacheValid = (view: 'received' | 'entered') => {
    const cache = loadDashboardCache(view);
    if (!cache) {
      
      return false;
    }
    
    // Check if cache has expired
    const now = Date.now();
    const cacheAge = now - cache.timestamp;
    if (cacheAge > CACHE_TTL) {
      return false;
    }
    
    // Check if sort order has changed (only invalidate if it's a meaningful change)
    if (cache.sortOrder !== sortOrder) {
      
      return false;
    }
    
    // Use the more lenient filter comparison
    if (!areFiltersEffectivelyEqual(cache.filters, filters)) {
      
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated && userEmail) {
      // Only clear caches on very first load, not on re-renders or filter changes
      if (isFirstLoad.current) {
        
        isFirstLoad.current = false;
        
        // Only clear caches if they're invalid
        if (!isCacheValid(activeView)) {
          
          debouncedRefresh(() => fetchMetrics());
        } else {
          
          // Use cached data directly without fetching
          const currentCache = loadDashboardCache(activeView);
          if (currentCache) {
            setUserGoodThings(currentCache.data);
            setHasMore(currentCache.data.length >= ITEMS_PER_PAGE);
          }
          setIsLoading(false);
          
          // IMPORTANT: Even when using cached data, we still need to calculate metrics
          // because metrics are user-specific and not cached with the entries
          debouncedRefresh(() => fetchMetrics());
        }
      }
      
      // Add event listener for focus to reload data when user returns to the tab
      // but only if the cache is stale (older than 5 minutes)
      const handleFocus = () => {
        if (!isCacheValid(activeView)) {
          
          debouncedRefresh(() => fetchMetrics());
        } else {
          
        }
      };
      
      window.addEventListener('focus', handleFocus);
      
      return () => {
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [authLoading, isAuthenticated, userEmail, activeView, debouncedRefresh]);

  const handleDialogSuccess = () => {
    setDialogOpen(false);
    // Queue the metrics refresh through the RefreshManager
    debouncedRefresh(() => fetchMetrics());
  };

  if (error) {
    return (
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="text-center mt-4">
          <Button onClick={() => debouncedRefresh(() => fetchMetrics())}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 md:mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Good Things</h1>
          <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">Track and celebrate our team's achievements</p>
          <LastSyncIndicator />
        </div>
        <div className="flex items-center gap-2">
          {/* Only show layout toggle on desktop */}
          <div className="hidden md:block">
            <LayoutToggle 
              onLayoutChange={setLayoutType} 
              className="mr-2"
            />
          </div>
          <GoodThingDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            onSuccess={handleDialogSuccess}
          />
        </div>
      </div>

      {/* User Metrics */}
      {isAuthenticated && userEmail && (
        <>
          {/* Recent User Good Things */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <Clock className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Your Good Things</h2>
            </div>
            
            {/* Metrics Section */}
            <div className="mb-6 md:mb-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
                <MetricCard
                  icon={Heart}
                  label="Good Things Received"
                  value={isLoadingMetrics.current ? <Skeleton className="h-6 w-12 mx-auto" /> : metrics.receivedCount}
                  color="bg-pink-100"
                />
                <MetricCard
                  icon={Send}
                  label="Good Things Entered"
                  value={isLoadingMetrics.current ? <Skeleton className="h-6 w-12 mx-auto" /> : metrics.enteredCount}
                  color="bg-blue-100"
                />
                <div className="col-span-2 md:col-span-1">
                  <MetricCard
                    icon={Clock}
                    label="Days Since Your Last Entry"
                    value={isLoadingMetrics.current ? <Skeleton className="h-6 w-12 mx-auto" /> : metrics.daysSinceLastEntry}
                    color="bg-purple-100"
                  />
                </div>
              </div>
            </div>

            {/* Filters */}
            <Collapsible
              open={isFiltersOpen}
              onOpenChange={setIsFiltersOpen}
              className="mb-6 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium dark:text-white">
                    Filters {(filters.category !== ALL_CATEGORIES || 
                             filters.enterer !== ALL_PEOPLE || 
                             filters.dateRange.from || 
                             filters.dateRange.to || 
                             sortOrder !== SORT_NEWEST) && 
                             `(${((filters.category !== ALL_CATEGORIES) ? 1 : 0) + 
                                ((filters.enterer !== ALL_PEOPLE) ? 1 : 0) + 
                                (filters.dateRange.from ? 1 : 0) + 
                                (sortOrder !== SORT_NEWEST ? 1 : 0)})`}
                  </span>
                </div>
                <ChevronDown className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isFiltersOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="p-4 border-t dark:border-gray-700 space-y-6">
                {/* Filter Controls */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* Sort Order */}
                  <div>
                    <h3 className="text-sm font-medium mb-2 dark:text-white">Sort Order</h3>
                    <Select
                      value={sortOrder}
                      onValueChange={(value) => {
                        setSortOrder(value);
                        // Refresh data with new sort order
                        debouncedRefresh(() => fetchMetrics());
                      }}
                    >
                      <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                        <SelectValue placeholder="Select sort order" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SORT_NEWEST}>Newest First</SelectItem>
                        <SelectItem value={SORT_OLDEST}>Oldest First</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Enterer Dropdown - Only shown for "Received" view */}
                  {activeView === 'received' && (
                    <div>
                      <h3 className="text-sm font-medium mb-2 dark:text-white">From</h3>
                      <Select
                        value={filters.enterer}
                        onValueChange={(value) => {
                          setFilters(prev => ({ ...prev, enterer: value }));
                          // Refresh data with new filter
                          debouncedRefresh(() => fetchMetrics());
                        }}
                      >
                        <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                          <SelectValue placeholder="Select an enterer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL_PEOPLE}>All People</SelectItem>
                          {enterers
                            .filter(person => person && person.trim() !== '')
                            .map(person => (
                              <SelectItem key={`enterer-${person}`} value={person}>
                                {formatName(person)}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Category Dropdown */}
                  <div>
                    <h3 className="text-sm font-medium mb-2 dark:text-white">Category</h3>
                    <Select
                      value={filters.category}
                      onValueChange={(value) => {
                        setFilters(prev => ({ ...prev, category: value }));
                        // Refresh data with new filter
                        debouncedRefresh(() => fetchMetrics());
                      }}
                    >
                      <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_CATEGORIES}>All Categories</SelectItem>
                        {categories
                          .filter(category => category && category.trim() !== '')
                          .map(category => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Preset Selector */}
                  <div>
                    <DatePresetSelector
                      fromDate={filters.dateRange.from}
                      toDate={filters.dateRange.to}
                      onDateRangeChange={(from, to, preset) => {
                        setFilters(prev => ({
                          ...prev,
                          dateRange: { from, to },
                          datePreset: preset
                        }));
                        // Refresh data with new date range
                        debouncedRefresh(() => fetchMetrics());
                      }}
                    />
                  </div>
                  {/* Clear Filters Button */}
                  <div className="flex items-end">
                    <Button 
                      variant="default"
                      size="sm"
                      onClick={handleClearFilters}
                      disabled={filters.category === ALL_CATEGORIES && 
                                filters.enterer === ALL_PEOPLE && 
                                !filters.dateRange.from && 
                                !filters.dateRange.to &&
                                sortOrder === SORT_NEWEST}
                      className="bg-pink-600 hover:bg-pink-700 w-full"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* View Toggle Buttons */}
            <div className="flex justify-center mb-6 md:mb-8">
              <div className="inline-flex rounded-md shadow-sm">
                <Button
                  variant={activeView === 'received' ? 'default' : 'outline'}
                  className={`rounded-r-none ${activeView === 'received' ? 'bg-pink-500 hover:bg-pink-600' : ''}`}
                  onClick={async () => {
                    if (activeView !== 'received') {
                      // Set loading state
                      setIsLoading(true);
                      setUserGoodThings([]);
                      
                      // Update the active view
                      setActiveView('received');
                      
                      try {
                        // Check if we have valid cached data
                        if (isCacheValid('received')) {
                          
                          const cachedData = loadDashboardCache('received');
                          if (cachedData) {
                            setUserGoodThings(cachedData.data);
                            setHasMore(cachedData.data.length >= ITEMS_PER_PAGE);
                            currentPage.current = 1;
                          }
                        } else {
                          // Fetch new data if cache is invalid
                          
                          const userName = await employeeNamesService.getNameFromEmail(userEmail!);
                          if (userName) {
                            const receivedGoodThings = await fetchUserReceivedGoodThings(userEmail!, ITEMS_PER_PAGE, 0);
                            
                            
                            // Update cache
                            saveDashboardCache('received', receivedGoodThings, filters, sortOrder);
                            
                            setUserGoodThings(receivedGoodThings);
                            setHasMore(receivedGoodThings.length >= ITEMS_PER_PAGE);
                            currentPage.current = 1;
                          }
                        }
                      } catch (err) {
                        
                      } finally {
                        setIsLoading(false);
                      }
                    }
                  }}
                >
                  Good Things You've Received
                </Button>
                <Button
                  variant={activeView === 'entered' ? 'default' : 'outline'}
                  className={`rounded-l-none ${activeView === 'entered' ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
                  onClick={async () => {
                    if (activeView !== 'entered') {
                      // Set loading state
                      setIsLoading(true);
                      setUserGoodThings([]);
                      
                      // Update the active view
                      setActiveView('entered');
                      
                      try {
                        // Check if we have valid cached data
                        if (isCacheValid('entered')) {
                          
                          const cachedData = loadDashboardCache('entered');
                          if (cachedData) {
                            setUserGoodThings(cachedData.data);
                            setHasMore(cachedData.data.length >= ITEMS_PER_PAGE);
                            currentPage.current = 1;
                          }
                        } else {
                          // Fetch new data if cache is invalid
                          
                          const userName = await employeeNamesService.getNameFromEmail(userEmail!);
                          if (userName) {
                            const enteredGoodThings = await fetchUserEnteredGoodThings(userName, ITEMS_PER_PAGE, 0);
                            
                            
                            // Update cache
                            saveDashboardCache('entered', enteredGoodThings, filters, sortOrder);
                            
                            setUserGoodThings(enteredGoodThings);
                            setHasMore(enteredGoodThings.length >= ITEMS_PER_PAGE);
                            currentPage.current = 1;
                          }
                        }
                      } catch (err) {
                        
                      } finally {
                        setIsLoading(false);
                      }
                    }
                  }}
                >
                  Good Things You've Entered
                </Button>
              </div>
            </div>

            {isLoading ? (
              layoutType === 'grid' ? (
                <GoodThingsGrid>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Card key={i}>
                      <CardContent className="p-3 md:p-6">
                        <Skeleton className="h-24 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </GoodThingsGrid>
              ) : (
                <GoodThingsLine>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Card key={i}>
                      <CardContent className="p-3 md:p-6">
                        <Skeleton className="h-24 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </GoodThingsLine>
              )
            ) : (
              <RecentUserGoodThings 
                goodThings={userGoodThings}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                layoutType={layoutType}
                onEdit={() => {
                  // Refresh the data after editing
                  debouncedRefresh(() => fetchMetrics());
                }}
                onDelete={() => {
                  // Refresh the data after deleting
                  debouncedRefresh(() => fetchMetrics());
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
